import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AnalyticsChart from '../../../shared/components/chart-table/AnalyticsChart';
import {
  fetchAnalyticsData,
  fetchWidgets,
  clearAnalyticsData,
  syncWidgetsWithBackend,
  selectAnalyticsLoading
} from '../../../../app/store/slices/adminSlice';
import { shouldSyncWithBackend } from '../../../../utils/widgetPersistence';
import ResizableWidgetContainer from '../../../shared/components/widgets/ResizableWidgetContainer';
import WidgetDisplay from '../../../shared/components/widgets/WidgetDisplay';
import { metrics, timeFilters } from '../../../../utils/constants/AnalyticsFilters'

const AnalyticsManagement = () => {
  const dispatch = useDispatch();
  const [timeFilter, setTimeFilter] = useState('month');
  const [selectedMetric, setSelectedMetric] = useState('usage');

  const loading = useSelector(state => state.admin.loading);
  const analyticsData = useSelector(state => state.admin.analyticsData);
  const analyticsLoading = useSelector(selectAnalyticsLoading);
  const widgets = useSelector(state => state.admin.widgets);
  const lastSyncTime = useSelector(state => state.admin.lastSyncTime);


  // Initial data fetch
  useEffect(() => {
    dispatch(fetchAnalyticsData({ type: selectedMetric, period: timeFilter }));
  }, [dispatch, selectedMetric, timeFilter]);

  // Load widgets on component mount
  useEffect(() => {
    dispatch(fetchWidgets()).unwrap()
    dispatch(fetchWidgets({ forceRefresh: shouldSyncWithBackend() }));
  }, [dispatch]);

  // Background sync every 2 minutes
  useEffect(() => {
    const syncInterval = setInterval(() => {
      if (shouldSyncWithBackend()) {
        dispatch(syncWidgetsWithBackend());
      }
    }, 30 * 60 * 1000); // 30 minutes

    return () => clearInterval(syncInterval);
  }, [dispatch]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && shouldSyncWithBackend()) {
        dispatch(fetchWidgets({ forceRefresh: true }));
      }
    };

    const handleFocus = () => {
      if (shouldSyncWithBackend()) {
        dispatch(fetchWidgets({ forceRefresh: true }));
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [dispatch]);

  const getTotalRevenue = () => {
    if (
      !analyticsLoading &&
      selectedMetric === 'revenue' &&
      analyticsData &&
      analyticsData.datasets &&
      analyticsData.datasets[0] &&
      Array.isArray(analyticsData.datasets[0].data) &&
      analyticsData.type === 'revenue'
    ) {
      return analyticsData.datasets[0].data.reduce((sum, val) => sum + val, 0);
    }
    return 0;
  };

  // Helper to get total user growth for the period
  const getTotalUserGrowth = () => {
    if (
      !analyticsLoading &&
      selectedMetric === 'user-growth' &&
      analyticsData &&
      analyticsData.datasets &&
      analyticsData.datasets[0] &&
      Array.isArray(analyticsData.datasets[0].data) &&
      analyticsData.type === 'user-growth'
    ) {
      return analyticsData.datasets[0].data.reduce((sum, val) => sum + val, 0);
    }
    return 0;
  };

  // Helper to get period display
  const getPeriodDisplay = () => {
    if (analyticsData && analyticsData.labels && analyticsData.labels.length > 0) {
      const start = analyticsData.labels[0];
      const end = analyticsData.labels[analyticsData.labels.length - 1];
      return `${new Date(start).toLocaleDateString()} - ${new Date(end).toLocaleDateString()}`;
    }
    return '';
  };

  // Clear analytics data when changing metrics
  const handleMetricChange = (metric) => {
    setSelectedMetric(metric);
    dispatch(clearAnalyticsData());
  };

  // Clear analytics data when changing time filter
  const handleTimeFilterChange = (filter) => {
    setTimeFilter(filter);
    dispatch(clearAnalyticsData());
  };

  // Filter widgets for admin context
  const adminWidgets = widgets.filter(widget => widget.contextType === 'admin');

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Sync Status Indicator */}
      {lastSyncTime && (
        <div className="text-xs text-gray-500 text-right">
          Last synced: {new Date(lastSyncTime).toLocaleTimeString()}
        </div>
      )}
      
      {/* Filters Section */}
      <div className="bg-white p-3 md:p-4 rounded-lg shadow">
        <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center">
          {/* Metrics Buttons */}
          <div className="flex flex-wrap gap-2">
            {metrics.map(metric => (
              <button
                key={metric.value}
                onClick={() => handleMetricChange(metric.value)}
                className={`px-3 py-1.5 md:px-4 md:py-2 text-sm md:text-base rounded-md transition-colors flex-1 sm:flex-none ${
                  selectedMetric === metric.value
                    ? 'bg-btn-primary text-white'
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                {metric.label}
              </button>
            ))}
          </div>

          {/* Time Filters */}
          <div className="flex flex-wrap gap-2">
            {timeFilters.map(filter => (
              <button
                key={filter.value}
                onClick={() => handleTimeFilterChange(filter.value)}
                className={`px-2 py-1 md:px-3 md:py-1.5 text-xs md:text-sm rounded-md transition-colors flex-1 sm:flex-none ${
                  timeFilter === filter.value
                    ? 'bg-btn-primary text-white'
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Analytics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Chart Section */}
        <div className="lg:col-span-2 bg-white p-3 md:p-6 rounded-lg shadow">
          {/* Revenue or User Growth Summary */}
          {!analyticsLoading && (selectedMetric === 'revenue' || selectedMetric === 'user-growth') && analyticsData && analyticsData.type === selectedMetric && (
            <div className="mb-4 flex items-center gap-4">
              {selectedMetric === 'revenue' && (
                <div className="flex items-center gap-2 bg-btn-secondary text-btn-primary px-4 py-2 rounded-lg font-semibold text-lg">
                  <svg width="22" height="22" fill="#0a363c" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17.93c-2.83.48-5.48-1.51-5.96-4.34-.09-.52.36-.99.89-.99.45 0 .83.33.89.77.34 2.13 2.5 3.45 4.5 2.83 1.21-.36 2.07-1.51 2.07-2.83 0-1.3-.86-2.45-2.07-2.82-1.99-.62-4.16.7-4.5 2.83-.06.44-.44.77-.89.77-.53 0-.98-.47-.89-.99.48-2.83 3.13-4.82 5.96-4.34 2.83.48 4.82 3.13 4.34 5.96-.48 2.83-3.13 4.82-5.96 4.34z"/>
                  </svg>
                  Total Revenue: <span className="ml-1">${getTotalRevenue().toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
              )}
              {selectedMetric === 'user-growth' && (
                <div className="flex items-center gap-2 bg-btn-secondary text-btn-primary px-4 py-2 rounded-lg font-semibold text-lg">
                  <svg width="22" height="22" fill="#0a363c" viewBox="0 0 24 24">
                    <path d="M12 12c2.7 0 8 1.34 8 4v2H4v-2c0-2.66 5.3-4 8-4zm0-2a4 4 0 100-8 4 4 0 000 8z"/>
                  </svg>
                  New Users: <span className="ml-1">{getTotalUserGrowth()}</span>
                </div>
              )}
            </div>
          )}

          {/* Chart */}
          <div className="h-[300px] md:h-[400px]">
            <AnalyticsChart
              data={analyticsData}
              type="line"
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                    labels: {
                      boxWidth: 12,
                      padding: 15,
                      font: {
                        size: window.innerWidth < 768 ? 10 : 12,
                        weight: 500
                      },
                      usePointStyle: true,
                      pointStyle: 'circle'
                    }
                  },
                  tooltip: {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    titleColor: '#0a363c',
                    bodyColor: '#0a363c',
                    titleFont: {
                      weight: 600,
                      size: 14
                    },
                    bodyFont: {
                      size: 13
                    },
                    padding: 12,
                    borderColor: '#e5f2f2', 
                    borderWidth: 1,
                    displayColors: true,
                    boxPadding: 6
                  }
                },
                scales: {
                  x: {
                    grid: {
                      color: '#f0f9f9',
                      drawBorder: true,
                      tickLength: 8
                    },
                    ticks: {
                      font: {
                        size: window.innerWidth < 768 ? 10 : 12
                      },
                      padding: 8,
                      color: '#0a363c'
                    }
                  },
                  y: {
                    grid: {
                      color: '#f0f9f9',
                      drawBorder: true
                    },
                    ticks: {
                      font: {
                        size: window.innerWidth < 768 ? 10 : 12,
                        weight: 500
                      },
                      padding: 8,
                      color: '#0a363c', 
                      callback: function(value) {
                        if (selectedMetric === 'revenue') {
                          return '$' + value;
                        }
                        return value;
                      }
                    },
                    beginAtZero: true
                  }
                },
                elements: {
                  line: {
                    tension: 0.3, 
                    borderWidth: 3,
                    fill: true,
                    backgroundColor: 'rgba(136, 222, 222, 0.2)'
                  },
                  point: {
                    radius: 4,
                    hoverRadius: 6,
                    borderWidth: 2,
                    hoverBorderWidth: 3
                  }
                },
                interaction: {
                  mode: 'index',
                  intersect: false
                },
                hover: {
                  mode: 'index',
                  intersect: false
                }
              }}
              loading={analyticsLoading}
              emptyText="No data available"
              height="100%"
            />
          </div>
          {/* Period display below the chart */}
          <div className="mt-2 text-xs md:text-sm text-btn-primary text-center">
            {getPeriodDisplay()}
          </div>
        </div>

        {/* Widgets Section - Show first widget in sidebar */}
        <div className="bg-white p-3 md:p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <h3 className="text-base md:text-lg font-semibold">Widget Preview</h3>
            <div className="text-xs text-gray-500">
              {adminWidgets.length} widget{adminWidgets.length !== 1 ? 's' : ''}
            </div>
          </div>
          
          {adminWidgets.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v6a2 2 0 002 2h4a2 2 0 002-2V5z" />
              </svg>
              <h4 className="text-sm font-medium text-gray-600 mb-2">No Widgets Yet</h4>
              <p className="text-xs text-gray-500 mb-3">
                Use CashBot to create analytics widgets
              </p>
              <div className="space-y-1 text-xs text-gray-400">
                <p>"Show me user statistics"</p>
                <p>"What's the revenue this month?"</p>
                <p>"Create a user growth chart"</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Show first widget in sidebar */}
              {adminWidgets.slice(0, 1).map((widget) => (
                <div key={widget.id} className="transform scale-95 origin-top">
                  <WidgetDisplay widget={widget} compact={false} context="admin" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Resizable Widget Grid*/}
      {adminWidgets.length > 1 && (
        <div className="bg-white p-4 md:p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg md:text-xl font-semibold text-gray-800">More Widgets</h3>
              <p className="text-sm text-gray-600 mt-1">
                Drag to reposition • Resize from bottom-right corner • Changes save automatically
              </p>
            </div>
            <div className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
              {adminWidgets.length - 1} More Widget{adminWidgets.length - 1 !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Render remaining widgets (skip first one) */}
          <ResizableWidgetContainer
            widgets={adminWidgets.slice(1)}
            context="admin"
            enableResize={true}
            enableDrag={true}
            compact={false}
          />
        </div>
      )}
    </div>
  );
};

export default AnalyticsManagement;